import { request } from 'utils/request';
import { header, getUUIDToken } from 'utils/auth';

const fetchTemplateGenerationSettingsFromAPI = async ({
  dealershipId,
}: {
  dealershipId: string;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/dealer-settings?dealer=${dealershipId}&type=TEMPLATE_GENERATION_CONFIG`;
    const response: any = await request(requestURL, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
    });

    if (!response) {
      throw new Error('Failed to fetch template generation settings from API');
    }
    return await response;
  } catch (error) {
    console.error('Error fetching template generation settings:', error);
    throw error;
  }
};

const updateTemplateGenerationSettingsFromAPI = async ({
  dealershipId,
  settingsId,
  data,
}: {
  dealershipId: string;
  settingsId: string;
  data: any;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/dealer-settings/${settingsId}?dealer=${dealershipId}`;
    const response: any = await request(requestURL, {
      method: 'PUT',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
      body: JSON.stringify(data),
    });

    if (!response) {
      throw new Error('Failed to update template generation settings from API');
    }
    return await response;
  } catch (error) {
    console.error('Error updating template generation settings:', error);
    throw error;
  }
};

const fetchSegmentsForDealerFromAPI = async (
  dealerId: string,
): Promise<any> => {
  try {
    const requestURL = `/api/v1/segments/${dealerId}`;
    const response: any = await request(requestURL, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
        'uuid-token': getUUIDToken(),
      }),
    });
    if (!response) throw new Error('Failed to fetch segments');
    return response;
  } catch (error) {
    console.error('Error fetching segments:', error);
    throw error;
  }
};

export const fetchTemplateGenerationSettings = async ({
  dealershipId,
}: {
  dealershipId: string;
}): Promise<any> => {
  return fetchTemplateGenerationSettingsFromAPI({ dealershipId });
};

export const fetchSegmentsForDealer = async (
  dealerId: string,
): Promise<any> => {
  return fetchSegmentsForDealerFromAPI(dealerId);
};

export const updateTemplateGenerationSettings = async ({
  dealershipId,
  settingsId,
  data,
}: {
  dealershipId: string;
  settingsId: string;
  data: any;
}): Promise<any> => {
  return updateTemplateGenerationSettingsFromAPI({
    dealershipId,
    settingsId,
    data,
  });
};

const initializeTemplateGenerationSettingsFromAPI = async ({
  dealerId,
}: {
  dealerId: string;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/dealer-settings/initialize?dealer_id=${dealerId}&type=TEMPLATE_GENERATION_CONFIG`;
    const response: any = await request(requestURL, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
    });

    if (!response) {
      throw new Error('Failed to initialize template generation settings from API');
    }
    const res = await response;
    const status = res?.statusCode ?? res?.myAutoIQ_status_code ?? res?.errorCode;
    const msg = res?.message ?? res?.myAutoIQ_error_message;
    if (typeof status === 'number' && status >= 400) {
      throw new Error(msg || 'Failed to initialize template generation settings');
    }
    return res;
  } catch (error) {
    console.error('Error initializing template generation settings:', error);
    throw error;
  }
};

export const initializeTemplateGenerationSettings = async ({
  dealerId,
}: {
  dealerId: string;
}): Promise<any> => {
  return initializeTemplateGenerationSettingsFromAPI({ dealerId });
};
